import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import VaultEntry from './components/VaultEntry';
import CommandCenter from './components/CommandCenter';
import CommandCenterNobel from './components/CommandCenterNobel';
import { contentPreloader } from './services/contentPreloader';
import { authService } from './services/authService';
import NobelDemo from './NobelDemo';
import { LastCallModal, RegressionAlert } from './components/modals';

const App: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isPreloading, setIsPreloading] = useState(true);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [useNobel, setUseNobel] = useState(true); // Use Nobel CommandCenter by default
  const [darkMode, setDarkMode] = useState(() => {
    // Check localStorage first, then OS preference
    const saved = localStorage.getItem('nobel-theme');
    if (saved) return saved === 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Psychological trigger states
  const [showLastCall, setShowLastCall] = useState(false);
  const [showRegression, setShowRegression] = useState(false);
  const [regressionData, setRegressionData] = useState<any>(null);

  // Dark mode effect
  useEffect(() => {
    const theme = darkMode ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('nobel-theme', theme);
  }, [darkMode]);

  useEffect(() => {
    const init = async () => {
      try {
        await authService.initialize();
        const user = await authService.getCurrentUser();
        if (user) {
          setIsAuthenticated(true);
          setCurrentUser(user);
        } else {
          // Default to Guest for direct access
          setCurrentUser(authService.getGuestUser());
        }
        await contentPreloader.initialize();
        setTimeout(() => {
          setIsPreloading(false);
          setIsLoaded(true);
        }, 2500);
      } catch (e) {
        console.error("Initialization failed", e);
        setIsPreloading(false);
        setIsLoaded(true);
      }
    };
    init();
    return () => contentPreloader.stopBackgroundWorker();
  }, []);

  const handleAuthenticate = async () => {
    const user = await authService.getCurrentUser();
    setCurrentUser(user);
    setIsAuthenticated(true);
    setShowLoginModal(false);
  };

  const handleLogout = async () => {
    await authService.signOut();
    setCurrentUser(authService.getGuestUser());
    setIsAuthenticated(false);
  };

  if (isPreloading) {
    return (
      <div className="min-h-screen bg-[#0f0f0f] flex items-center justify-center relative overflow-hidden shaded-area">
        <div className="scanline opacity-10"></div>
        <div className="text-center relative z-10">
          <motion.div
            animate={{ scale: [1, 1.1, 1], opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 2.5, repeat: Infinity }}
            className="text-7xl mb-10 drop-shadow-[0_0_30px_rgba(197,160,89,0.3)]"
          >
            ⚡
          </motion.div>
          <h1 className="text-5xl font-black text-[#C5A059] tracking-[0.4em] mb-6 mono uppercase drop-shadow-lg">Ted-Talks-DZ</h1>
          <p className="text-stone-600 mono text-[9px] tracking-[0.5em] font-black uppercase mb-12">Initializing Visual Warfare Protocol...</p>
          <div className="w-80 h-1 bg-stone-900 rounded-full mx-auto overflow-hidden shadow-inner">
            <motion.div
              className="h-full bg-gradient-to-r from-[#C5A059] via-orange-500 to-[#C5A059]"
              initial={{ width: '0%' }}
              animate={{ width: '100%' }}
              transition={{ duration: 3, ease: "easeInOut" }}
            />
          </div>
        </div>
      </div>
    );
  }

  if (!isLoaded) return null;

  return (
    <div style={{ backgroundColor: 'var(--bg-primary)', minHeight: '100vh', fontFamily: "'Space Grotesk', sans-serif" }}>
      <CommandCenterNobel
        onLogout={handleLogout}
        onLogin={() => setShowLoginModal(true)}
        currentUser={currentUser}
        darkMode={darkMode}
        onToggleDarkMode={() => setDarkMode(!darkMode)}
      />

      {showLoginModal && (
        <VaultEntry
          onAuthenticate={handleAuthenticate}
          onClose={() => setShowLoginModal(false)}
        />
      )}

      {/* Psychological Trigger Modals */}
      {showRegression && regressionData && (
        <RegressionAlert
          activityType={regressionData.activityType}
          currentScore={regressionData.currentScore}
          previousAvg={regressionData.previousAvg}
          decline={regressionData.decline}
          onBoardGate={() => setShowRegression(false)}
          onViewHistory={() => setShowRegression(false)}
          onDismiss={() => setShowRegression(false)}
        />
      )}
    </div>
  );
};

export default App;